import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Paperclip, Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  createWoundAssessment,
  deleteWoundAssessment,
  deleteWoundAttachment,
  getWoundAssessments,
  getWoundAttachmentUrl,
  getWoundAttachments,
  updateWoundAssessment,
  uploadWoundAttachment,
} from '../../services/api';
import type {
  TaskListResponse,
  WoundAssessmentCreate,
  WoundAssessmentResponse,
  WoundAttachmentResponse,
  WoundResponse,
} from '../../services/api/types.gen';
import { getWoundTypeLabel } from '../../constants/wounds';
import { getConflictCode } from '../../utils/apiErrorDetail';
import { isBrowserRenderableImage } from '../../utils/browserImage';
import { useStore } from '../../store';
import WoundAssessmentDialog from './WoundAssessmentDialog';
import WoundAssessmentCard from './WoundAssessmentCard';
import DeleteAssessmentDialog from './DeleteAssessmentDialog';
import WoundImageLightbox from './WoundImageLightbox';
import WoundAttachmentRow from './WoundAttachmentRow';
import { openWoundAttachment } from './openWoundAttachment';
import type { WoundImage } from './useWoundImages';

interface WoundAssessmentsDrawerProps {
  wound: WoundResponse | null;
  open: boolean;
  readOnly?: boolean;
  visitId?: string;
  visits?: TaskListResponse[];
  /** The parent is still loading `visits`; hold the cards until it lands. */
  visitsLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fired after a successful assessment create/update/delete so the chart
   * visit picker can refresh per-visit wound activity labels.
   */
  onAssessmentsChanged?: () => void;
}

/** A wound holds one assessment per visit, so a repeat save is refused server
    side. Keyed off the conflict code, never the server's own message. */
const DUPLICATE_ASSESSMENT_MESSAGE =
  'This wound already has an assessment for this visit. Edit that assessment instead of adding another.';
const SAVE_FAILED_MESSAGE = 'Failed to save assessment. Please try again.';
const UPDATE_FAILED_MESSAGE = 'Failed to update assessment. Please try again.';

const WoundAssessmentsDrawer: React.FC<WoundAssessmentsDrawerProps> = ({
  wound,
  open,
  readOnly = false,
  visitId,
  visits = [],
  visitsLoading = false,
  onOpenChange,
  onAssessmentsChanged,
}) => {
  const [assessments, setAssessments] = useState<WoundAssessmentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAssessmentDialogOpen, setIsAssessmentDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingAssessment, setEditingAssessment] =
    useState<WoundAssessmentResponse | null>(null);
  const [previousAssessment, setPreviousAssessment] =
    useState<WoundAssessmentResponse | null>(null);

  const [deletingAssessment, setDeletingAssessment] =
    useState<WoundAssessmentResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Attachments (all for this wound, grouped by assessment_id in the UI)
  const [attachments, setAttachments] = useState<WoundAttachmentResponse[]>([]);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState<
    Record<string, string>
  >({});
  const [attachmentPreviewFailedIds, setAttachmentPreviewFailedIds] = useState<
    string[]
  >([]);
  // The drawer previews the one attachment a clinician clicked, so the gallery
  // gets a single-image list and hides its navigation.
  const [lightboxImage, setLightboxImage] = useState<WoundImage | null>(null);
  const [uploadingForAssessment, setUploadingForAssessment] = useState<
    string | null
  >(null);

  const showError = useStore(state => state.showError);
  const attachmentErrorToastShownRef = useRef(false);

  // One assessment per wound per visit, so on this visit the second one is an
  // edit of the first. The server refuses the duplicate either way; hiding the
  // path stops a clinician charting a form that cannot be saved.
  const hasAssessmentForVisit =
    !!visitId && assessments.some(a => a.visit_id === visitId);

  // A file whose assessment link is empty (an imported photo whose task had
  // no assessment on this wound) belongs to no card. Without its own group it
  // would be stored on the wound and shown nowhere.
  const assessmentIds = new Set(assessments.map(a => a.id));
  const unlinkedAttachments = attachments.filter(
    a => !a.assessment_id || !assessmentIds.has(a.assessment_id)
  );

  const fetchAssessments = useCallback(async () => {
    if (!wound) return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getWoundAssessments({
        path: { patient_id: wound.patient_id, wound_id: wound.id },
      });
      setAssessments((resp.data as WoundAssessmentResponse[]) ?? []);
    } catch {
      setError('Failed to load assessments');
    } finally {
      setIsLoading(false);
    }
  }, [wound]);

  const fetchAttachments = useCallback(async () => {
    if (!wound) return;
    try {
      const resp = await getWoundAttachments({
        path: { patient_id: wound.patient_id, wound_id: wound.id },
      });
      const list = (resp.data as WoundAttachmentResponse[]) ?? [];
      setAttachments(list);
      const imageAtts = list.filter(a => isBrowserRenderableImage(a.mime_type));
      const failedIds: string[] = [];
      const urlEntries = await Promise.all(
        imageAtts.map(async att => {
          try {
            const urlResp = await getWoundAttachmentUrl({
              path: {
                patient_id: wound.patient_id,
                wound_id: wound.id,
                attachment_id: att.id,
              },
            });
            const data = urlResp.data as { url?: string };
            if (data?.url) {
              return [att.id, data.url] as const;
            }
            failedIds.push(att.id);
            return null;
          } catch (err) {
            console.error('Failed to load wound image preview:', err);
            failedIds.push(att.id);
            return null;
          }
        })
      );
      setAttachmentPreviewUrls(
        Object.fromEntries(
          urlEntries.filter((e): e is readonly [string, string] => e !== null)
        )
      );
      setAttachmentPreviewFailedIds(failedIds);
      if (failedIds.length > 0 && !attachmentErrorToastShownRef.current) {
        attachmentErrorToastShownRef.current = true;
        showError(
          'Unable to load image preview',
          'Some wound images could not be displayed. Please refresh or try again.'
        );
      }
    } catch (err) {
      console.error('Failed to load wound attachments:', err);
      if (!attachmentErrorToastShownRef.current) {
        attachmentErrorToastShownRef.current = true;
        showError(
          'Unable to load attachments',
          'Something went wrong. Please try again.'
        );
      }
    }
  }, [wound, showError]);

  useEffect(() => {
    if (open && wound) {
      fetchAssessments();
      fetchAttachments();
    } else {
      setAssessments([]);
      setAttachments([]);
      setAttachmentPreviewUrls({});
      setAttachmentPreviewFailedIds([]);
      setLightboxImage(null);
      setError(null);
      setSaveError(null);
      attachmentErrorToastShownRef.current = false;
    }
  }, [open, wound, fetchAssessments, fetchAttachments]);

  const handleUploadAttachment = async (assessmentId: string, file: File) => {
    if (!wound) return;
    setUploadingForAssessment(assessmentId);
    try {
      await uploadWoundAttachment({
        path: { patient_id: wound.patient_id, wound_id: wound.id },
        query: { assessment_id: assessmentId },
        body: { file },
      });
      await fetchAttachments();
    } catch (err) {
      console.error('Failed to upload attachment:', err);
      showError('Failed to upload attachment. Please try again.');
    } finally {
      setUploadingForAssessment(null);
    }
  };

  const handleDownloadAttachment = async (
    attachment: WoundAttachmentResponse
  ) => {
    if (!wound) return;
    await openWoundAttachment(
      wound,
      attachment,
      showError,
      'Failed to download attachment. Please try again.'
    );
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!wound) return;
    try {
      await deleteWoundAttachment({
        path: {
          patient_id: wound.patient_id,
          wound_id: wound.id,
          attachment_id: attachmentId,
        },
      });
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      showError('Failed to delete attachment. Please try again.');
    }
  };

  const handlePreviewAttachment = (
    attachment: WoundAttachmentResponse,
    url: string
  ) =>
    setLightboxImage({
      attachmentId: attachment.id,
      fileName: attachment.file_name,
      url,
    });

  const handleSaveAssessment = async (data: WoundAssessmentCreate) => {
    if (!wound) return;
    const genericMessage = editingAssessment
      ? UPDATE_FAILED_MESSAGE
      : SAVE_FAILED_MESSAGE;
    try {
      const response = editingAssessment
        ? await updateWoundAssessment({
            path: {
              patient_id: wound.patient_id,
              wound_id: wound.id,
              assessment_id: editingAssessment.id,
            },
            body: data,
          })
        : await createWoundAssessment({
            path: { patient_id: wound.patient_id, wound_id: wound.id },
            body: data,
          });

      // The client has no throwOnError, so a refused save resolves with
      // `error`. Keep the dialog open and say why: closing it would discard
      // the observations the clinician just charted with no explanation.
      if (response.error) {
        const isDuplicate =
          getConflictCode(response.error) === 'WOUND_ASSESSMENT_EXISTS';
        setSaveError(
          isDuplicate ? DUPLICATE_ASSESSMENT_MESSAGE : genericMessage
        );
        if (isDuplicate) {
          // The server only refuses a duplicate when this list is stale, so the
          // assessment the message points at is not on screen yet. Pull it in
          // behind the dialog, which also disables Add Assessment.
          await fetchAssessments();
        }
        return;
      }

      setSaveError(null);
      setIsAssessmentDialogOpen(false);
      setEditingAssessment(null);
      setPreviousAssessment(null);
      fetchAssessments();
      onAssessmentsChanged?.();
    } catch (err) {
      console.error('Failed to save assessment:', err);
      setSaveError(genericMessage);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!wound || !deletingAssessment) return;
    setIsDeleting(true);
    try {
      const response = await deleteWoundAssessment({
        path: {
          patient_id: wound.patient_id,
          wound_id: wound.id,
          assessment_id: deletingAssessment.id,
        },
      });
      // Client has no throwOnError; a failed delete returns { error }. Don't
      // close the confirm dialog or refresh visit activity on failure.
      if (response.error) {
        showError('Failed to delete assessment. Please try again.');
        return;
      }
      setDeletingAssessment(null);
      fetchAssessments();
      // The server deletes the assessment's files with it. Without a reload
      // they would linger here as "Not linked to a visit".
      fetchAttachments();
      onAssessmentsChanged?.();
    } catch (err) {
      console.error('Failed to delete assessment:', err);
      showError('Failed to delete assessment. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='w-[min(920px,92vw)] max-w-[920px] max-h-[85vh] overflow-y-auto'>
          <div className='mb-3 flex items-center justify-between border-b pb-3 pr-8'>
            <DialogHeader className='space-y-0'>
              <DialogTitle className='text-base'>
                {wound
                  ? `${wound.location} — ${getWoundTypeLabel(wound.wound_type)}`
                  : 'Assessments'}
              </DialogTitle>
            </DialogHeader>
            {!readOnly && (
              <div className='shrink-0'>
                <Button
                  size='sm'
                  disabled={hasAssessmentForVisit}
                  title={
                    hasAssessmentForVisit
                      ? 'This wound already has an assessment for this visit'
                      : undefined
                  }
                  onClick={() => {
                    setEditingAssessment(null);
                    setPreviousAssessment(null);
                    setSaveError(null);
                    setIsAssessmentDialogOpen(true);
                  }}
                >
                  <Plus className='h-4 w-4 mr-1' />
                  Add Assessment
                </Button>
              </div>
            )}
          </div>

          <div className='space-y-3'>
            {isLoading || visitsLoading ? (
              <div className='flex justify-center py-8'>
                <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              </div>
            ) : error ? (
              <p className='text-sm text-destructive text-center py-4'>
                {error}
              </p>
            ) : assessments.length === 0 ? (
              <p className='text-sm text-muted-foreground text-center py-8'>
                No assessments recorded yet
              </p>
            ) : (
              <div className='grid gap-3 md:grid-cols-2'>
                {assessments.map(assessment => (
                  <WoundAssessmentCard
                    key={assessment.id}
                    assessment={assessment}
                    attachments={attachments.filter(
                      a => a.assessment_id === assessment.id
                    )}
                    attachmentPreviewUrls={attachmentPreviewUrls}
                    attachmentPreviewFailedIds={attachmentPreviewFailedIds}
                    readOnly={readOnly}
                    visits={visits}
                    isUploadingAttachment={
                      uploadingForAssessment === assessment.id
                    }
                    onEdit={() => {
                      setEditingAssessment(assessment);
                      setPreviousAssessment(null);
                      setSaveError(null);
                      setIsAssessmentDialogOpen(true);
                    }}
                    onDelete={() => setDeletingAssessment(assessment)}
                    onUploadAttachment={file =>
                      handleUploadAttachment(assessment.id, file)
                    }
                    onDownloadAttachment={handleDownloadAttachment}
                    onDeleteAttachment={handleDeleteAttachment}
                    onPreviewAttachment={handlePreviewAttachment}
                  />
                ))}
              </div>
            )}
            {!isLoading &&
              !visitsLoading &&
              !error &&
              unlinkedAttachments.length > 0 && (
                <section
                  aria-labelledby='wound-unlinked-attachments'
                  className='border rounded-md p-3 space-y-1.5 bg-card'
                >
                  <h3
                    id='wound-unlinked-attachments'
                    className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1'
                  >
                    <Paperclip className='h-3 w-3' aria-hidden='true' />
                    Not linked to a visit
                    <span className='ml-0.5'>
                      ({unlinkedAttachments.length})
                    </span>
                  </h3>
                  <div className='space-y-1'>
                    {unlinkedAttachments.map(att => (
                      <WoundAttachmentRow
                        key={att.id}
                        attachment={att}
                        previewUrl={attachmentPreviewUrls[att.id]}
                        previewUnavailable={attachmentPreviewFailedIds.includes(
                          att.id
                        )}
                        onDownload={handleDownloadAttachment}
                        onDelete={readOnly ? undefined : handleDeleteAttachment}
                        onPreview={handlePreviewAttachment}
                      />
                    ))}
                  </div>
                </section>
              )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assessment form dialog */}
      {wound && (
        <WoundAssessmentDialog
          open={isAssessmentDialogOpen}
          onOpenChange={setIsAssessmentDialogOpen}
          woundId={wound.id}
          visitId={visitId}
          editingAssessment={editingAssessment}
          previousAssessment={previousAssessment}
          saveError={saveError}
          onSave={handleSaveAssessment}
        />
      )}

      <DeleteAssessmentDialog
        open={!!deletingAssessment}
        isDeleting={isDeleting}
        onConfirm={handleDeleteAssessment}
        onClose={() => setDeletingAssessment(null)}
      />

      <WoundImageLightbox
        images={lightboxImage ? [lightboxImage] : []}
        index={lightboxImage ? 0 : null}
        onIndexChange={() => {}}
        onClose={() => setLightboxImage(null)}
      />
    </>
  );
};

export default WoundAssessmentsDrawer;
